/**
 * LeadquellenSettings — CRUD-Verwaltung der Leadquellen
 * (Admin-Einstellungen → Sonstiges → Leadquellen), nach dem Muster von
 * AbbruchGruendeSettings.
 *
 * Die hier gepflegten Quellen füllen das Dropdown „Quelle" im
 * „Neue Anfrage"-Drawer (/anfragen). Jede Quelle hat ein Icon (Emoji — Klick
 * ins Feld öffnet die Auswahl <EmojiPickerField/>, Freitext bleibt möglich),
 * einen Namen und eine Kampagne (Freitext).
 *
 * Endpoints: GET/POST/PUT/DELETE /v2/admin/lead-quellen. Es ist derselbe
 * Katalog, den der öffentliche Lead-Webhook per firstOrCreate befüllt —
 * deshalb ist `name` eindeutig und der Server antwortet bei Dubletten 422.
 */
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, SectionTitle, Button, Field, Input, Badge } from '../../components/ui';
import { getThemeColors } from '../../lib/theme';
import { EmojiPickerField } from '../../components/EmojiPicker';
import { v2LeadQuellen } from '../../lib/v1';
import type { LeadQuelle } from '../../lib/v1';

interface LeadquellenSettingsProps {
  dark: boolean;
}

interface EditState {
  name: string;
  icon: string;
  kampagne: string;
  aktiv: boolean;
}

const EMPTY_STATE: EditState = { name: '', icon: '', kampagne: '', aktiv: true };

export function LeadquellenSettings({ dark }: LeadquellenSettingsProps) {
  const t = getThemeColors(dark);
  const { t: tr } = useTranslation();

  const [quellen, setQuellen] = useState<LeadQuelle[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [editId, setEditId] = useState<number | null>(null);
  const [editState, setEditState] = useState<EditState>(EMPTY_STATE);
  const [editError, setEditError] = useState<string | null>(null);
  const [editSaving, setEditSaving] = useState(false);

  const [showAddForm, setShowAddForm] = useState(false);
  const [addState, setAddState] = useState<EditState>(EMPTY_STATE);
  const [addError, setAddError] = useState<string | null>(null);
  const [addSaving, setAddSaving] = useState(false);

  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    setLoadError(null);
    try {
      // all=true: Einstellungen zeigen auch inaktive Quellen.
      const data = await v2LeadQuellen.list(true);
      setQuellen(data.slice().sort((a, b) => a.sort - b.sort || a.id - b.id));
    } catch {
      setLoadError(tr('settings.lead_quellen.load_error'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Up/Down: sort-Werte der beiden Nachbarn tauschen (2 Updates).
  const swap = async (idx: number, dir: -1 | 1) => {
    const other = idx + dir;
    if (other < 0 || other >= quellen.length) return;
    const a = quellen[idx];
    const b = quellen[other];
    const next = [...quellen];
    [next[idx], next[other]] = [b, a];
    setQuellen(next);
    try {
      await Promise.all([
        v2LeadQuellen.update(a.id, { sort: b.sort }),
        v2LeadQuellen.update(b.id, { sort: a.sort }),
      ]);
      void load();
    } catch {
      void load();
    }
  };

  const openEdit = (q: LeadQuelle) => {
    setEditId(q.id);
    setEditState({
      name: q.name,
      icon: q.icon ?? '',
      kampagne: q.kampagne ?? '',
      aktiv: q.aktiv,
    });
    setEditError(null);
  };

  /**
   * Der Server ist bei `name` die letzte Instanz (unique). Eine 422 hier als
   * generisches „Speichern fehlgeschlagen" zu zeigen, würde den Grund
   * verschlucken — deshalb die Feldmeldung durchreichen.
   */
  const saveErrorText = (e: unknown, fallbackKey: string): string => {
    const err = e as { response?: { status?: number; data?: { errors?: Record<string, string[]> } } };
    if (err?.response?.status === 422) {
      const nameErrors = err.response.data?.errors?.name;
      if (nameErrors && nameErrors.length > 0) return tr('settings.lead_quellen.error.duplicate');
    }
    return tr(fallbackKey);
  };

  const saveEdit = async () => {
    if (editId === null) return;
    if (!editState.name.trim()) {
      setEditError(tr('settings.lead_quellen.error.name_required'));
      return;
    }
    setEditSaving(true);
    setEditError(null);
    try {
      const updated = await v2LeadQuellen.update(editId, {
        name: editState.name.trim(),
        icon: editState.icon.trim() || null,
        kampagne: editState.kampagne.trim() || null,
        aktiv: editState.aktiv,
      });
      setQuellen(prev => prev.map(q => q.id === editId ? updated : q));
      setEditId(null);
    } catch (e) {
      setEditError(saveErrorText(e, 'settings.lead_quellen.error.save'));
    } finally {
      setEditSaving(false);
    }
  };

  /**
   * Löschen ist bewusst mit Rückfrage: eine Quelle, an der Leads hängen, lehnt
   * der Server mit 409 ab (`leads.quelle_id` ist nullOnDelete — ein Löschen
   * würde deren Herkunft verlieren). Für diesen Fall zeigen wir den Weg über
   * „inaktiv" statt einer generischen Fehlermeldung.
   */
  const handleDelete = async (q: LeadQuelle) => {
    if (!window.confirm(tr('settings.lead_quellen.delete_confirm', { name: q.name }))) return;
    setDeletingId(q.id);
    setDeleteError(null);
    try {
      await v2LeadQuellen.remove(q.id);
      setQuellen(prev => prev.filter(x => x.id !== q.id));
    } catch (e) {
      const err = e as { response?: { status?: number; data?: { meta?: { lead_count?: number } } } };
      if (err?.response?.status === 409) {
        setDeleteError(tr('settings.lead_quellen.error.in_use', {
          name: q.name,
          count: err.response.data?.meta?.lead_count ?? 0,
        }));
      } else {
        setDeleteError(tr('settings.lead_quellen.error.delete'));
      }
    } finally {
      setDeletingId(null);
    }
  };

  const handleAdd = async () => {
    if (!addState.name.trim()) {
      setAddError(tr('settings.lead_quellen.error.name_required'));
      return;
    }
    setAddSaving(true);
    setAddError(null);
    try {
      const created = await v2LeadQuellen.create({
        name: addState.name.trim(),
        icon: addState.icon.trim() || null,
        kampagne: addState.kampagne.trim() || null,
        aktiv: addState.aktiv,
      });
      setQuellen(prev => [...prev, created]);
      setAddState(EMPTY_STATE);
      setShowAddForm(false);
    } catch (e) {
      setAddError(saveErrorText(e, 'settings.lead_quellen.error.create'));
    } finally {
      setAddSaving(false);
    }
  };

  const INPUT_STYLE: React.CSSProperties = {
    border: `1px solid ${t.border}`, borderRadius: 8,
    padding: '7px 10px', fontSize: 13, color: t.heading,
    background: t.inputBg, fontFamily: 'Outfit, sans-serif',
    outline: 'none', width: '100%',
  };

  const toggleButtons = (
    value: boolean,
    onChange: (v: boolean) => void,
    labels: [string, string],
  ) => (
    <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
      {[true, false].map(v => (
        <button
          key={String(v)}
          onClick={() => onChange(v)}
          style={{
            flex: 1, padding: '8px 0', borderRadius: 8, border: `1px solid ${value === v ? '#22c55e' : t.border}`,
            background: value === v ? '#ecfdf3' : t.card,
            color: value === v ? '#027a48' : t.text,
            cursor: 'pointer', fontSize: 12, fontWeight: 500, fontFamily: 'Outfit, sans-serif',
          }}
        >
          {v ? labels[0] : labels[1]}
        </button>
      ))}
    </div>
  );

  const editForm = (
    state: EditState,
    setState: React.Dispatch<React.SetStateAction<EditState>>,
    error: string | null,
    saving: boolean,
    onSave: () => void,
    onCancel: () => void,
    heading?: string,
  ) => (
    <div style={{
      padding: 14, background: t.cardMuted, border: `1px solid ${t.borderSoft}`,
      borderRadius: 10, marginBottom: 8, marginTop: heading ? 14 : 0,
      display: 'flex', flexDirection: 'column', gap: 10,
    }}>
      {heading && (
        <div style={{ fontSize: 13, fontWeight: 600, color: t.heading, marginBottom: 4 }}>{heading}</div>
      )}
      <div style={{ display: 'grid', gridTemplateColumns: '80px 1fr 1fr', gap: 10 }}>
        <Field label={tr('settings.lead_quellen.field.icon')}>
          {/* Klick ins Feld öffnet die Emoji-Auswahl; Tippen bleibt erlaubt. */}
          <EmojiPickerField
            value={state.icon}
            onChange={v => setState(s => ({ ...s, icon: v }))}
            dark={dark}
            label={tr('settings.lead_quellen.field.icon')}
            placeholder={tr('settings.lead_quellen.field.icon_placeholder')}
            inputStyle={INPUT_STYLE}
          />
        </Field>
        <Field label={tr('settings.lead_quellen.field.name')}>
          {/* `Field` rendert sein <label> als Geschwister ohne htmlFor — ohne
              aria-label hätte das Pflichtfeld keinen zugänglichen Namen. */}
          <Input
            value={state.name}
            onChange={e => setState(s => ({ ...s, name: e.target.value }))}
            dark={dark}
            placeholder={tr('settings.lead_quellen.field.name_placeholder')}
            aria-label={tr('settings.lead_quellen.field.name')}
          />
        </Field>
        <Field label={tr('settings.lead_quellen.field.kampagne')}>
          <input
            value={state.kampagne}
            onChange={e => setState(s => ({ ...s, kampagne: e.target.value }))}
            style={INPUT_STYLE}
            placeholder={tr('settings.lead_quellen.field.kampagne_placeholder')}
            aria-label={tr('settings.lead_quellen.field.kampagne')}
          />
        </Field>
      </div>

      <Field label={tr('settings.lead_quellen.field.status')}>
        {toggleButtons(
          state.aktiv,
          v => setState(s => ({ ...s, aktiv: v })),
          [tr('settings.lead_quellen.active'), tr('settings.lead_quellen.inactive')],
        )}
      </Field>

      {error && <div style={{ fontSize: 12, color: '#b42318' }}>{error}</div>}

      <div style={{ display: 'flex', gap: 8 }}>
        <Button variant="primary" size="sm" dark={dark} disabled={saving} onClick={onSave}>
          {saving ? tr('settings.lead_quellen.saving') : tr('settings.lead_quellen.save')}
        </Button>
        <Button variant="secondary" size="sm" dark={dark} onClick={onCancel}>
          {tr('settings.lead_quellen.cancel')}
        </Button>
      </div>
    </div>
  );

  if (loading) {
    return (
      <Card dark={dark} style={{ padding: 20 }}>
        <div style={{ color: t.muted, fontSize: 13 }}>{tr('settings.lead_quellen.loading')}</div>
      </Card>
    );
  }

  if (loadError) {
    return (
      <Card dark={dark} style={{ padding: 20 }}>
        <div style={{ color: '#b42318', fontSize: 13, marginBottom: 12 }}>{loadError}</div>
        <Button variant="secondary" size="sm" dark={dark} onClick={() => void load()}>
          {tr('settings.lead_quellen.retry')}
        </Button>
      </Card>
    );
  }

  return (
    <Card dark={dark} style={{ padding: 20 }}>
      <SectionTitle
        dark={dark}
        title={tr('settings.lead_quellen.title')}
        subtitle={tr('settings.lead_quellen.subtitle')}
      />

      {quellen.length === 0 && (
        <div style={{ fontSize: 13, color: t.muted, padding: '10px 0' }}>
          {tr('settings.lead_quellen.empty')}
        </div>
      )}

      {quellen.map((q, idx) => (
        <div key={q.id}>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 10,
            padding: '10px 0',
            borderBottom: `1px solid ${t.borderSoft}`,
            flexWrap: 'wrap',
          }}>
            <span style={{ fontSize: 18, width: 26, textAlign: 'center' }} aria-hidden="true">
              {q.icon || '•'}
            </span>

            <div style={{ flex: 1, minWidth: 100 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: t.heading }}>{q.name}</div>
              {q.kampagne && (
                <div style={{
                  fontSize: 11, color: t.muted, marginTop: 2,
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 260,
                }}>
                  {q.kampagne}
                </div>
              )}
            </div>

            <Badge tone={q.aktiv ? 'success' : 'neutral'} dark={dark}>
              {q.aktiv ? tr('settings.lead_quellen.active') : tr('settings.lead_quellen.inactive')}
            </Badge>

            <button
              aria-label={tr('settings.lead_quellen.up')}
              disabled={idx === 0}
              onClick={() => void swap(idx, -1)}
              style={{
                background: 'none', border: `1px solid ${t.border}`, borderRadius: 6,
                padding: '3px 8px', cursor: idx === 0 ? 'not-allowed' : 'pointer',
                color: idx === 0 ? t.muted : t.text, fontSize: 12,
                fontFamily: 'Outfit, sans-serif',
              }}
            >↑</button>
            <button
              aria-label={tr('settings.lead_quellen.down')}
              disabled={idx === quellen.length - 1}
              onClick={() => void swap(idx, 1)}
              style={{
                background: 'none', border: `1px solid ${t.border}`, borderRadius: 6,
                padding: '3px 8px', cursor: idx === quellen.length - 1 ? 'not-allowed' : 'pointer',
                color: idx === quellen.length - 1 ? t.muted : t.text, fontSize: 12,
                fontFamily: 'Outfit, sans-serif',
              }}
            >↓</button>

            <Button variant="secondary" size="sm" dark={dark} onClick={() => openEdit(q)}>
              {tr('settings.lead_quellen.edit')}
            </Button>

            <Button
              variant="secondary" size="sm" dark={dark}
              disabled={deletingId === q.id}
              onClick={() => void handleDelete(q)}
            >
              {deletingId === q.id ? '…' : tr('settings.lead_quellen.delete')}
            </Button>
          </div>

          {editId === q.id && editForm(
            editState, setEditState, editError, editSaving,
            () => void saveEdit(), () => { setEditId(null); setEditError(null); },
          )}
        </div>
      ))}

      {deleteError && (
        <div style={{ fontSize: 12, color: '#b42318', marginTop: 6 }}>{deleteError}</div>
      )}

      {showAddForm ? (
        editForm(
          addState, setAddState, addError, addSaving,
          () => void handleAdd(), () => { setShowAddForm(false); setAddError(null); },
          tr('settings.lead_quellen.new_heading'),
        )
      ) : (
        <div style={{ marginTop: 14 }}>
          <Button variant="primary" size="sm" dark={dark} onClick={() => setShowAddForm(true)}>
            {tr('settings.lead_quellen.add')}
          </Button>
        </div>
      )}
    </Card>
  );
}
